function setHidden(el, isHidden) {
  if (isHidden) {
    el.setAttribute('hidden', '');
  } else {
    el.removeAttribute('hidden');
  }
}

if (!customElements.get('audience-selector-media')) {
  customElements.define(
    'audience-selector-media',
    class AudienceSelectorMedia extends HTMLElement {
      constructor() {
        super();
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.isIntersecting = false;
        this.evaluate = this.evaluate.bind(this);
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this.isIntersecting = entry.isIntersecting;
          this.evaluate();
        });
        this.observer.observe(this);

        this.desktopQuery.addEventListener('change', this.evaluate);
        this.reduceMotionQuery.addEventListener('change', this.evaluate);
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        this.desktopQuery.removeEventListener('change', this.evaluate);
        this.reduceMotionQuery.removeEventListener('change', this.evaluate);
      }

      evaluate() {
        if (!this.video) return;

        if (!this.isIntersecting) {
          this.video.pause();
          return;
        }

        if (this.video.dataset.userPaused === 'true') {
          return;
        }

        const userStarted = this.video.dataset.userStarted === 'true';
        const autoEligible = this.desktopQuery.matches && !this.reduceMotionQuery.matches;

        if (userStarted || autoEligible) {
          this.video.play();
        }
      }
    }
  );
}

/*
  <audience-corridor>: four barely-visible diagonals, from the section corners
  to the corners of the recessed content panel (.audience-selector__well), so
  the section reads as a hallway leading to the four paths. Each line starts
  at full strength at the section corner and fades out as it reaches the panel
  (a gradient along its own length). Fades in on first view; with reduced
  motion it is drawn in place. Redrawn on resize.
*/
if (!customElements.get('audience-corridor')) {
  customElements.define(
    'audience-corridor',
    class AudienceCorridor extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.audience-selector');
        this.svg = this.querySelector('svg');
        this.panel = this.section?.querySelector('.audience-selector__well');
        if (!this.section || !this.svg || !this.panel) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.progress = reduced || window.Shopify?.designMode ? 1 : 0;

        this.resizeObserver = new ResizeObserver(() => this.draw());
        this.resizeObserver.observe(this.section);
        this.draw();

        if (this.progress < 1) {
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.intersectionObserver.disconnect();
              this.open();
            },
            { threshold: 0.2 }
          );
          this.intersectionObserver.observe(this.section);
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
      }

      open() {
        if (!window.gsap) {
          this.progress = 1;
          this.draw();
          return;
        }
        window.gsap.to(this, { progress: 1, duration: 1.8, ease: 'power2.out', onUpdate: () => this.draw() });
      }

      draw() {
        const s = this.section.getBoundingClientRect();
        const p = this.panel.getBoundingClientRect();
        const W = s.width;
        const H = s.height;
        if (!W || !H) return;

        const wall = {
          l: p.left - s.left,
          t: p.top - s.top,
          r: p.right - s.left,
          b: p.bottom - s.top,
        };
        const f = (n) => n.toFixed(1);
        const id = this.gradientId || (this.gradientId = `corridor-${Math.random().toString(36).slice(2, 8)}`);

        // Section corner → panel corner.
        const lines = [
          [0, 0, wall.l, wall.t],
          [W, 0, wall.r, wall.t],
          [0, H, wall.l, wall.b],
          [W, H, wall.r, wall.b],
        ];

        // One gradient per line, laid along it: strongest at the section corner,
        // fading out as it reaches the panel (like depth haze down the hallway).
        const defs = lines
          .map(
            ([x1, y1, x2, y2], i) =>
              `<linearGradient id="${id}-${i}" gradientUnits="userSpaceOnUse" x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}">` +
              `<stop offset="0" stop-opacity="1"/><stop offset="1" stop-opacity="0"/>` +
              `</linearGradient>`
          )
          .join('');
        const paths = lines
          .map(([x1, y1, x2, y2], i) => `<path stroke="url(#${id}-${i})" d="M${f(x1)} ${f(y1)}L${f(x2)} ${f(y2)}"/>`)
          .join('');

        this.svg.setAttribute('viewBox', `0 0 ${f(W)} ${f(H)}`);
        this.svg.style.opacity = this.progress;
        this.svg.innerHTML = `<defs>${defs}</defs>${paths}`;
      }
    }
  );
}

/*
  <audience-sequence>: a photo sequence that plays like footage. Each frame
  gets a slow camera move while it is on screen (push in, pan, turn, pull
  out), and frames hand over with a rack-focus dissolve (the incoming one
  comes into focus, the outgoing one softens and fades). All motion is Web
  Animations, so pausing just pauses every running animation in the element.

  It behaves like a <video> for the shared toggle: play(), pause(), .paused,
  'play'/'pause' events and the same dataset flags (userPaused/userStarted).
  Plays while on screen and the tab is visible. With reduced motion it does
  not start on its own; if started, frames only crossfade (no camera move).
*/
if (!customElements.get('audience-sequence')) {
  const MOVES = [
    // [from, to, transform-origin], one per frame (cycled).
    ['scale(1.04) translate3d(0, 1.5%, 0)', 'scale(1.14) translate3d(0, -1.5%, 0)'], // push in, rising
    ['scale(1.14) translate3d(3%, 0, 0)', 'scale(1.14) translate3d(-3%, 0, 0)'], // pan, left to right
    ['scale(1.08) rotate(-1.5deg)', 'scale(1.18) rotate(1.5deg)'], // slow turn (beans swirling)
    ['scale(1.18) translate3d(-1.5%, -1%, 0)', 'scale(1.05) translate3d(1%, 1%, 0)'], // pull out
    ['scale(1.03)', 'scale(1.13)'], // push in to the cup
  ];
  const FADE = 2400;

  customElements.define(
    'audience-sequence',
    class AudienceSequence extends HTMLElement {
      constructor() {
        super();
        this.paused = true;
        this.index = 0;
        this.isIntersecting = false;
        this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.evaluate = this.evaluate.bind(this);
      }

      connectedCallback() {
        this.frames = [...this.querySelectorAll('[data-frame]')];
        this.hold = parseFloat(this.dataset.hold) || 5000;
        if (this.frames.length < 2) return;

        // Only the first frame loads with the page (the others have no src
        // yet, see audience-selector.liquid). Each frame then fetches the next
        // one while it holds, so a slow connection never pays for photos the
        // visitor hasn't reached.
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.isIntersecting = entry.isIntersecting;
            if (entry.isIntersecting) this.loadFrame(this.index + 1);
            this.evaluate();
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this);
        document.addEventListener('visibilitychange', this.evaluate);
        this.reduceMotionQuery.addEventListener('change', this.evaluate);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.evaluate);
        this.reduceMotionQuery.removeEventListener('change', this.evaluate);
        this.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
      }

      // Gives frame i its real sources (srcset before src, so the browser picks
      // the right width) and lets it load now.
      loadFrame(i) {
        const img = this.frames[i % this.frames.length]?.querySelector('img');
        if (!img) return null;
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
        }
        img.loading = 'eager';
        return img;
      }

      evaluate() {
        if (this.frames.length < 2) return;
        const visible = this.isIntersecting && !document.hidden;
        if (!visible) {
          this.halt();
          return;
        }
        if (this.dataset.userPaused === 'true') return;
        if (this.dataset.userStarted === 'true' || !this.reduceMotionQuery.matches) this.play();
      }

      // Public, video-like API ------------------------------------------------
      play() {
        if (!this.paused) return Promise.resolve();
        this.paused = false;
        if (this.clock) {
          this.getAnimations({ subtree: true }).forEach((animation) => animation.play());
        } else {
          this.startFrame(this.index, false);
        }
        this.dispatchEvent(new Event('play'));
        return Promise.resolve();
      }

      pause() {
        if (this.paused) return;
        this.halt();
        this.dispatchEvent(new Event('pause'));
      }

      // Pauses without announcing it (off screen / hidden tab): the toggle keeps
      // showing "pause" because the visitor did not ask for it.
      halt() {
        this.paused = true;
        this.getAnimations({ subtree: true }).forEach((animation) => animation.pause());
      }

      // Frames ------------------------------------------------------------------
      startFrame(i, withTransition) {
        const reduced = this.reduceMotionQuery.matches;
        const frame = this.frames[i];
        const img = frame.querySelector('img');
        const [from, to, origin = '50% 50%'] = MOVES[i % MOVES.length];
        if (img) img.style.transformOrigin = origin;
        const previousIndex = (i - 1 + this.frames.length) % this.frames.length;

        // Only the incoming frame (on top) and the outgoing one stay active;
        // anything left over from an interrupted swap is cleared here, so the
        // order never depends on a finish event arriving.
        this.frames.forEach((other, j) => {
          const keep = j === i || (withTransition && j === previousIndex);
          other.style.zIndex = j === i ? '1' : '0';
          if (!keep) {
            other.classList.remove('is-active');
            other.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
          }
        });
        img?.getAnimations().forEach((animation) => animation.cancel());

        if (img && !reduced) {
          img.animate([{ transform: from }, { transform: to }], {
            duration: this.hold + FADE * 2,
            easing: 'linear',
            fill: 'both',
          });
        }

        if (withTransition) {
          const previous = this.frames[previousIndex];
          frame.classList.add('is-active');
          frame.animate(
            reduced
              ? [{ opacity: 0 }, { opacity: 1 }]
              : [
                  { opacity: 0, filter: 'blur(14px)' },
                  { opacity: 1, filter: 'blur(0px)' },
                ],
            { duration: FADE, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' }
          );
          const out = previous.animate(
            reduced
              ? [{ opacity: 1 }, { opacity: 0 }]
              : [
                  { opacity: 1, filter: 'blur(0px) brightness(1)' },
                  { opacity: 0, filter: 'blur(8px) brightness(1.12)' },
                ],
            { duration: FADE, easing: 'cubic-bezier(0.45, 0, 0.55, 1)' }
          );
          out.onfinish = () => {
            previous.classList.remove('is-active');
            previous.querySelector('img')?.getAnimations().forEach((animation) => animation.cancel());
          };
        }

        // The clock: an empty animation as long as the frame is held; when it
        // ends, the next frame comes in (pauses with everything else).
        this.clock = this.animate([], { duration: this.hold });
        this.clock.onfinish = () => this.next();
      }

      async next() {
        const nextIndex = (this.index + 1) % this.frames.length;
        const img = this.loadFrame(nextIndex);
        if (img && !img.complete) {
          // Wait for the next photo, but never stall the sequence on a slow
          // network: after 4 s it moves on and the photo appears once loaded.
          await Promise.race([
            img.decode().catch(() => {}),
            new Promise((resolve) => setTimeout(resolve, 4000)),
          ]);
        }
        if (this.paused) {
          // Paused while the next image was loading: resume from here later.
          this.clock = this.animate([], { duration: 1 });
          this.clock.pause();
          this.clock.onfinish = () => this.next();
          return;
        }
        this.index = nextIndex;
        this.startFrame(nextIndex, true);
        // The one after loads while this frame holds.
        this.loadFrame(nextIndex + 1);
      }
    }
  );
}

if (!customElements.get('audience-selector-media-toggle')) {
  customElements.define(
    'audience-selector-media-toggle',
    class AudienceSelectorMediaToggle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.playIcon = this.querySelector('[data-icon="play"]');
        this.pauseIcon = this.querySelector('[data-icon="pause"]');
        this.playLabel = this.querySelector('[data-label="play"]');
        this.pauseLabel = this.querySelector('[data-label="pause"]');
        this.video = this.button
          ? document.getElementById(this.button.getAttribute('aria-controls'))
          : null;
        this.onClick = this.onClick.bind(this);
        this.onVideoPlay = this.onVideoPlay.bind(this);
        this.onVideoPause = this.onVideoPause.bind(this);

        if (this.button) {
          this.button.addEventListener('click', this.onClick);
        }

        if (this.video) {
          this.video.addEventListener('play', this.onVideoPlay);
          this.video.addEventListener('pause', this.onVideoPause);
        }
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this.onClick);
        }
        if (this.video) {
          this.video.removeEventListener('play', this.onVideoPlay);
          this.video.removeEventListener('pause', this.onVideoPause);
        }
      }

      onClick() {
        if (!this.video) return;

        if (this.video.paused) {
          this.video.dataset.userPaused = 'false';
          this.video.dataset.userStarted = 'true';
          this.video.play();
        } else {
          this.video.dataset.userPaused = 'true';
          this.video.pause();
        }
      }

      onVideoPlay() {
        setHidden(this.playIcon, true);
        setHidden(this.pauseIcon, false);
        setHidden(this.playLabel, true);
        setHidden(this.pauseLabel, false);
      }

      onVideoPause() {
        setHidden(this.playIcon, false);
        setHidden(this.pauseIcon, true);
        setHidden(this.playLabel, false);
        setHidden(this.pauseLabel, true);
      }
    }
  );
}
